import { useEffect, useState, type FormEvent } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, Alert, Box,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import type { SymptomReading } from '@lorrainemarwood-care/shared';
import { api } from '../lib/api';
import ScorePicker, { type ScoreDirection } from './ScorePicker';

type Key = 'breathlessness' | 'pain' | 'sleep' | 'appetite' | 'mood' | 'appearance';

const FIELDS: Array<{ key: Key; label: string; hint: string; direction: ScoreDirection }> = [
  { key: 'breathlessness', label: 'Breathlessness',    hint: '0 easy · 10 severe',      direction: 'rising-bad'  },
  { key: 'pain',           label: 'Pain',              hint: '0 none · 10 worst',       direction: 'rising-bad'  },
  { key: 'sleep',          label: 'Sleep',             hint: '0 poor · 10 great',       direction: 'rising-good' },
  { key: 'appetite',       label: 'Appetite',          hint: '0 none · 10 normal',      direction: 'rising-good' },
  { key: 'mood',           label: 'Mood',              hint: '0 low · 10 bright',       direction: 'rising-good' },
  { key: 'appearance',     label: 'How does she look', hint: '0 withdrawn · 10 bright', direction: 'rising-good' },
];

interface Props {
  reading: SymptomReading | null;
  open: boolean;
  onClose: () => void;
}

type State = Record<Key, number | null>;

export default function SymptomEditDialog({ reading, open, onClose }: Props) {
  const [occurredAt, setOccurredAt] = useState<Dayjs | null>(null);
  const [values, setValues] = useState<State>({
    breathlessness: null, pain: null, sleep: null, appetite: null, mood: null, appearance: null,
  });
  const [notes, setNotes] = useState('');
  const qc = useQueryClient();

  useEffect(() => {
    if (reading) {
      setOccurredAt(dayjs(reading.occurredAt));
      setValues({
        breathlessness: reading.breathlessness,
        pain: reading.pain,
        sleep: reading.sleep,
        appetite: reading.appetite,
        mood: reading.mood,
        appearance: reading.appearance,
      });
      setNotes(reading.notes ?? '');
    }
  }, [reading]);

  const mutation = useMutation({
    mutationFn: () => api.patch<SymptomReading>(`/api/symptoms/${reading!.id}`, {
      occurredAt: occurredAt?.toISOString(),
      ...values,
      notes: notes.trim() || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['symptoms'] });
      onClose();
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!occurredAt) return;
    mutation.mutate();
  };

  if (!reading) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit symptom reading</DialogTitle>
      <Box component="form" onSubmit={onSubmit}>
        <DialogContent>
          <Stack spacing={2.5}>
            <DateTimePicker
              label="When"
              value={occurredAt}
              onChange={setOccurredAt}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
            {FIELDS.map((f) => (
              <ScorePicker
                key={f.key}
                label={f.label}
                hint={f.hint}
                value={values[f.key]}
                onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))}
                direction={f.direction}
              />
            ))}
            <TextField
              label="Notes"
              multiline
              minRows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            {mutation.isError && <Alert severity="error">{(mutation.error as Error).message}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending || !occurredAt}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
